import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  Upload,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { eventService, CreateEventData, Event } from '../services/events';
import type { UploadFile } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Alert } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { staffService, Staff } from '../services/staffs';
import { customerService, Customer } from '../services/customers';
import { businessService, Business } from '../services/businesses';

const { TextArea } = Input;
const { Title } = Typography;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const EventFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'calendar.update');
  const canDelete = hasPermission(user, 'calendar.delete');
  const [isEditing, setIsEditing] = useState(!isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<UploadFile[]>([]);
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [loadingStaffs, setLoadingStaffs] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [eventRecord, setEventRecord] = useState<Event | null>(null);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Calendar
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  useEffect(() => {
    setIsEditing(!isEdit);
    if (isEdit && id) {
      loadEvent(parseInt(id, 10));
    } else {
      form.setFieldsValue({
        type: 'appointment',
        customer_type: 'personal',
        all_day: false,
        alert: false,
      });
    }
  }, [isEdit, id, form]);

  useEffect(() => {
    loadStaffs();
    loadCustomers();
    loadBusinesses();
  }, []);

  useEffect(() => {
    const notice = (location.state as { notice?: { type: 'success' | 'failure'; message: string } } | null)?.notice;
    if (notice) {
      setSubmitNotice(notice);
    }
  }, [location.state]);

  const loadStaffs = async () => {
    setLoadingStaffs(true);
    try {
      const response = await staffService.getAll();
      setStaffs(Array.isArray(response) ? response : []);
    } catch {
      message.error('Failed to load staffs');
    } finally {
      setLoadingStaffs(false);
    }
  };

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const response = await customerService.getAll();
      setCustomers(response.data || []);
    } catch {
      message.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const loadBusinesses = async () => {
    setLoadingBusinesses(true);
    try {
      const response = await businessService.getAll();
      setBusinesses(response.data || []);
    } catch {
      message.error('Failed to load businesses');
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const applyEventToForm = (event: Event) => {
    const resolvedCustomerType = event.customer_type
      || (event.business_id ? 'business' : event.customer_id ? 'personal' : undefined);
    form.setFieldsValue({
      ...event,
      customer_type: resolvedCustomerType,
      start: dayjs(event.start),
      end: dayjs(event.end),
    });

    if (event.attachment) {
      setAttachmentFile([{
        uid: '-1',
        name: 'attachment',
        status: 'done',
        url: `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'}/storage/${event.attachment}`,
      }]);
    } else {
      setAttachmentFile([]);
    }
  };

  const loadEvent = async (eventId: number) => {
    setLoadingEvent(true);
    try {
      const event = await eventService.getById(eventId);
      setEventRecord(event);
      applyEventToForm(event);
    } catch {
      message.error('Failed to load event');
      navigate('/calendar');
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      
      // Handle attachment
      if (attachmentFile.length > 0 && attachmentFile[0].originFileObj) {
        formData.append('attachment', attachmentFile[0].originFileObj);
      } else if (isEdit && attachmentFile.length === 0) {
        // If editing and no file, send empty string to remove attachment
        formData.append('attachment', '');
      }

      // Handle other fields
      Object.keys(values).forEach(key => {
        if (key === 'attachment') {
          // Skip, handled above
          return;
        } else if (key === 'start' || key === 'end') {
          if (values[key]) {
            formData.append(key, values[key].format('YYYY-MM-DD HH:mm:ss'));
          }
        } else if (values[key] !== undefined && values[key] !== null) {
          if (typeof values[key] === 'boolean') {
            formData.append(key, values[key] ? '1' : '0');
          } else {
            formData.append(key, values[key].toString());
          }
        }
      });

      let eventId: number;
      if (isEdit && id) {
        const updated = await eventService.update(parseInt(id, 10), formData);
        eventId = updated.id;
        setEventRecord(updated);
      } else {
        const created = await eventService.create(formData);
        eventId = created.id;
      }

      const notice = {
        type: 'success' as const,
        message: isEdit ? 'Event updated successfully.' : 'Event created successfully.',
      };
      setSubmitNotice(notice);

      if (!isEdit) {
        navigate(`/calendar/events/${eventId}/edit`, { state: { notice } });
      } else {
        setIsEditing(false);
        if (eventId) {
          await loadEvent(eventId);
        }
      }
    } catch (error: any) {
      const fallbackMessage = isEdit ? 'Failed to update event.' : 'Failed to create event.';
      const apiMessage = error?.response?.data?.message || error?.message;
      setSubmitNotice({
        type: 'failure',
        message: apiMessage ? `${fallbackMessage} ${apiMessage}` : fallbackMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !id) return;
    
    try {
      await eventService.delete(parseInt(id, 10));
      message.success('Event deleted successfully');
      navigate('/calendar');
    } catch {
      message.error('Failed to delete event');
    }
  };

  const handleCancelEdit = () => {
    if (eventRecord) {
      applyEventToForm(eventRecord);
    }
    setIsEditing(false);
  };

  const isViewMode = isEdit && !isEditing;

  const formatDateTime = (value?: Dayjs | string) => {
    if (!value) return '-';
    const parsed = typeof value === 'string' ? dayjs(value) : value;
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm:ss') : '-';
  };

  const formatDateOnly = (value?: Dayjs | string) => {
    if (!value) return '-';
    const parsed = typeof value === 'string' ? dayjs(value) : value;
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
  };

  const getStaffName = (staffId?: number | null) => {
    if (!staffId) return '-';
    return staffs.find((staff) => staff.id === staffId)?.name || '-';
  };

  const getCustomerName = (customerId?: number | null) => {
    if (!customerId) return '-';
    return customers.find((customer) => customer.id === customerId)?.name || '-';
  };

  const getBusinessName = (businessId?: number | null) => {
    if (!businessId) return '-';
    return businesses.find((business) => business.id === businessId)?.name || '-';
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          Event
        </Title>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/calendar')}>
            Back
          </Button>
          {isEdit && canUpdate && !isEditing && (
            <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
          {isEdit && isEditing && canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              Delete
            </Button>
          )}
        </Space>
      </div>


      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingEvent || (isEdit && !isEditing)}
      >
        <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Event Type</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.type !== next.type} noStyle>
                      {({ getFieldValue }) => {
                        const type = getFieldValue('type');
                        return type === 'leave' ? 'Leave' : 'Appointment';
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="type"
                      rules={[{ required: true, message: 'Please select type' }]}
                      style={{ margin: 0 }}
                    >
                      <Select
                        placeholder="Select Event Type"
                        onChange={(value) => {
                          if (value === 'leave') {
                            form.setFieldsValue({
                              customer_type: undefined,
                              customer_id: undefined,
                              business_id: undefined,
                            });
                          } else if (!form.getFieldValue('customer_type')) {
                            form.setFieldsValue({ customer_type: 'personal' });
                          }
                        }}
                      >
                        <Select.Option value="appointment">Appointment</Select.Option>
                        <Select.Option value="leave">Leave</Select.Option>
                      </Select>
                    </Form.Item>
                  )}
                </td>
              </tr>
              <Form.Item shouldUpdate={(prev, next) => prev.type !== next.type} noStyle>
                {({ getFieldValue }) => {
                  if (getFieldValue('type') === 'leave') {
                    return null;
                  }
                  return (
                    <>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Customer Type</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {isViewMode ? (
                            <Form.Item shouldUpdate={(prev, next) => prev.customer_type !== next.customer_type} noStyle>
                              {({ getFieldValue: getCustomerType }) => {
                                const type = getCustomerType('customer_type');
                                return type === 'business' ? 'Business' : type === 'personal' ? 'Personal' : '-';
                              }}
                            </Form.Item>
                          ) : (
                            <Form.Item
                              name="customer_type"
                              rules={[{ required: true, message: 'Please select customer type' }]}
                              style={{ margin: 0 }}
                            >
                              <Select
                                placeholder="Select Customer Type"
                                onChange={() => {
                                  form.setFieldsValue({ customer_id: undefined, business_id: undefined });
                                }}
                              >
                                <Select.Option value="personal">Personal</Select.Option>
                                <Select.Option value="business">Business</Select.Option>
                              </Select>
                            </Form.Item>
                          )}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                          <strong>Customer</strong>
                        </td>
                        <td style={{ padding: '12px', verticalAlign: 'top' }}>
                          {isViewMode ? (
                            <Form.Item shouldUpdate={(prev, next) => (
                              prev.customer_type !== next.customer_type
                              || prev.customer_id !== next.customer_id
                              || prev.business_id !== next.business_id
                            )} noStyle>
                              {({ getFieldValue: getCustomerType }) => {
                                const customerType = getCustomerType('customer_type');
                                if (customerType === 'business') {
                                  return getBusinessName(getCustomerType('business_id'));
                                }
                                return getCustomerName(getCustomerType('customer_id'));
                              }}
                            </Form.Item>
                          ) : (
                            <Form.Item shouldUpdate={(prev, next) => prev.customer_type !== next.customer_type} noStyle>
                              {({ getFieldValue: getCustomerType }) => {
                                const customerType = getCustomerType('customer_type');
                                if (customerType === 'business') {
                                  return (
                                    <Form.Item
                                      name="business_id"
                                      rules={[{ required: true, message: 'Please select business' }]}
                                      style={{ margin: 0 }}
                                    >
                                      <Select
                                        placeholder="Select Business"
                                        loading={loadingBusinesses}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        options={businesses.map((business) => ({
                                          value: business.id,
                                          label: business.name,
                                        }))}
                                      />
                                    </Form.Item>
                                  );
                                }

                                return (
                                  <Form.Item
                                    name="customer_id"
                                    rules={[{ required: true, message: 'Please select personal' }]}
                                    style={{ margin: 0 }}
                                  >
                                    <Select
                                      placeholder="Select Personal"
                                      loading={loadingCustomers}
                                      allowClear
                                      showSearch
                                      optionFilterProp="label"
                                      options={customers.map((customer) => ({
                                        value: customer.id,
                                        label: customer.name,
                                      }))}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          )}
                        </td>
                      </tr>
                    </>
                  );
                }}
              </Form.Item>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Staff</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.staff_id !== next.staff_id} noStyle>
                      {({ getFieldValue }) => getStaffName(getFieldValue('staff_id'))}
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="staff_id"
                      dependencies={['type']}
                      rules={[
                        ({ getFieldValue }) => ({
                          required: ['appointment', 'leave'].includes(getFieldValue('type')),
                          message: 'Please select staff',
                        }),
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Select
                        placeholder="Select Staff"
                        loading={loadingStaffs}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={staffs.map((staff) => ({
                          value: staff.id,
                          label: staff.name,
                        }))}
                      />
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Location</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.location !== next.location} noStyle>
                      {({ getFieldValue }) => getFieldValue('location') || '-'}
                    </Form.Item>
                  ) : (
                    <Form.Item name="location" style={{ margin: 0 }}>
                      <Input placeholder="Enter Location" />
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>All-day</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.all_day !== next.all_day} noStyle>
                      {({ getFieldValue }) => (getFieldValue('all_day') ? 'Yes' : 'No')}
                    </Form.Item>
                  ) : (
                    <Form.Item name="all_day" valuePropName="checked" style={{ margin: 0 }}>
                      <Switch
                        onChange={(checked) => {
                          if (checked) {
                            const startValue = form.getFieldValue('start');
                            if (startValue) {
                              form.setFieldsValue({ end: startValue });
                            }
                          }
                        }}
                      />
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Start</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.start !== next.start || prev.all_day !== next.all_day} noStyle>
                      {({ getFieldValue }) => {
                        const isAllDay = Boolean(getFieldValue('all_day'));
                        const startValue = getFieldValue('start');
                        return isAllDay ? formatDateOnly(startValue) : formatDateTime(startValue);
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item shouldUpdate={(prev, next) => prev.all_day !== next.all_day} noStyle>
                      {({ getFieldValue }) => {
                        const isAllDay = Boolean(getFieldValue('all_day'));
                        return (
                          <Form.Item
                            name="start"
                            rules={[{ required: true, message: 'Please select start time' }]}
                            style={{ margin: 0 }}
                          >
                            <DatePicker
                              showTime={!isAllDay}
                              format={isAllDay ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm:ss'}
                              style={{ width: '100%' }}
                              placeholder={isAllDay ? 'Select Date' : 'Enter Start Time'}
                              onChange={(value) => {
                                if (isAllDay) {
                                  form.setFieldsValue({ end: value });
                                }
                              }}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>End</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.end !== next.end || prev.all_day !== next.all_day} noStyle>
                      {({ getFieldValue }) => {
                        const isAllDay = Boolean(getFieldValue('all_day'));
                        const endValue = getFieldValue('end');
                        return isAllDay ? formatDateOnly(endValue) : formatDateTime(endValue);
                      }}
                    </Form.Item>
                  ) : (
                    <Form.Item shouldUpdate={(prev, next) => prev.all_day !== next.all_day} noStyle>
                      {({ getFieldValue }) => {
                        const isAllDay = Boolean(getFieldValue('all_day'));
                        return (
                          <Form.Item
                            name="end"
                            rules={[{ required: true, message: 'Please select end time' }]}
                            style={{ margin: 0 }}
                          >
                            <DatePicker
                              showTime={!isAllDay}
                              format={isAllDay ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm:ss'}
                              style={{ width: '100%' }}
                              placeholder={isAllDay ? 'Auto-filled' : 'Enter End Time'}
                              disabled={isAllDay}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Alert</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.alert !== next.alert} noStyle>
                      {({ getFieldValue }) => (getFieldValue('alert') ? 'Yes' : 'No')}
                    </Form.Item>
                  ) : (
                    <Form.Item name="alert" valuePropName="checked" style={{ margin: 0 }}>
                      <Switch />
                    </Form.Item>
                  )}
                </td>
              </tr>

              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Attachment</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    attachmentFile.length > 0 ? (
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => {
                          const file = attachmentFile[0];
                          if (file.url) {
                            window.open(file.url, '_blank');
                          }
                        }}
                      >
                        View Attachment
                      </Button>
                    ) : (
                      '-'
                    )
                  ) : (
                    <Upload
                      fileList={attachmentFile}
                      beforeUpload={() => false}
                      onChange={(info) => {
                        setAttachmentFile(info.fileList);
                      }}
                      maxCount={1}
                      accept=".pdf,.png,.jpg,.jpeg"
                      onRemove={() => setAttachmentFile([])}
                    >
                      <Button icon={<UploadOutlined />}>Upload PDF / PNG file</Button>
                    </Upload>
                  )}
                </td>
              </tr>

              <tr>
                <td style={{ padding: '12px', width: '160px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Notes</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isViewMode ? (
                    <Form.Item shouldUpdate={(prev, next) => prev.note !== next.note} noStyle>
                      {({ getFieldValue }) => getFieldValue('note') || '-'}
                    </Form.Item>
                  ) : (
                    <Form.Item name="note" style={{ margin: 0 }}>
                      <TextArea rows={4} placeholder="Enter notes here" />
                    </Form.Item>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Card>

        {(!isEdit || isEditing) && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 24,
              position: 'sticky',
              bottom: 0,
              background: '#fff',
              zIndex: 1,
            }}
          >
            <Space>
              <Button onClick={isEdit ? handleCancelEdit : () => navigate('/calendar')}>
                Cancel
              </Button>
              {(!isEdit || canUpdate) && (
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Save
                </Button>
              )}
            </Space>
          </div>
        )}
      </Form>
    </Card>
  );
};
